("use strict");

import storage from "./storage.js";
import events from "./event.js";
import { getConfiguration } from "@nuskin/configuration-sdk";
import RunConfigService from "./runConfigService.js";

import axios from "axios";
import * as lodash from "lodash";

/**
 *
 * StringService
 * Service to get common strings from /content/localization/strings/common
 *
 * @namespace StringService
 */
export default {
    getApplicationStrings,
    getString,
    getStrings,
    getPsvString,
    setHost
};

let host = RunConfigService.getBaseUrl();

let currentLocale = "";
let currentStrings = {};

let STRINGS_STORAGE_KEY = "shop-strings-";
let EXPIRE_TIME = 1000 * 60 * 20; // 20 minutes

async function getApplicationStrings(
    application,
    defaultResponse = {},
    locale = getLocale()
) {
    let applicationStrings;
    if (storage) {
        applicationStrings = storage.getItem({
            storageType: "sessionStorage",
            key: `${application}-strings-${locale}`
        });
    }
    if (!applicationStrings) {
        applicationStrings = await loadApplicationServerStrings(
            application,
            locale
        );
    }
    applicationStrings = lodash.merge(defaultResponse, applicationStrings);
    return applicationStrings;
}

/**
 * Gets a string value for the requested string
 *
 * @param stringCd - key for the string to get
 * @param dfltStr - if not found, the string to return
 * @param locale - locale of the string
 */
async function getString(stringCd, dfltStr = "", locale = getLocale()) {
    await refreshStrings(locale);
    return getRequestedStrings([{ key: stringCd, dflt: dfltStr }])[stringCd];
}

/**
 * Gets string values for a list of requested strings
 *
 * @param stringInfos - [{key: value, dflt:value},{key:value, dflt:value}...]
 * @param locale - locale of the string
 */
async function getStrings(stringInfos, locale = getLocale()) {
    await refreshStrings(locale);
    return getRequestedStrings(stringInfos);
}

/**
 * Helper function to get the PSV label.  Can be different based on account type.
 *
 * @param accountType
 * @param locale
 */
async function getPsvString(accountType, locale = getLocale()) {
    let psvStr;

    await refreshStrings(locale);
    switch (accountType) {
        case "10":
            psvStr = await getString("psvDist", "PSV", locale);
            break;
        case "20":
            psvStr = await getString("psvCust", "Product Value", locale);
            break;
        case "30":
            psvStr = await getString("psvPfc", "PSV", locale);
            break;
        default:
            psvStr = await getString("psvAnon", "Product Value", locale);
            break;
    }
    return psvStr;
}

/**
 * Used to get the locale if a locale is not passed in.
 *
 * @returns {string}
 */
function getLocale() {
    const runConfig = RunConfigService.getRunConfig();
    return `${runConfig.language}_${runConfig.country}`;
}

/**
 * Grabs the requested strings from the set of common strings.
 *
 * @param stringInfos
 */
function getRequestedStrings(stringInfos) {
    let returnVal = {};
    stringInfos.forEach((stringInfo) => {
        const key = stringInfo.key;
        returnVal[key] = currentStrings[key] || stringInfo.dflt;
    });
    return returnVal;
}

/**
 * This is called to insure that common strings are loaded either from localStorage
 *  or from the service. This could be called multiple times so a promise is returned
 *  and checked on subsequent while the first service call is being made.
 *
 * @param locale
 * @returns {Promise<void>}
 */
async function refreshStrings(locale) {
    if (locale !== currentLocale || lodash.isEmpty(currentStrings)) {
        currentLocale = locale;
        currentStrings = getStoredStrings(locale);
        if (!currentStrings) {
            currentStrings = await getServerStrings(locale);
        }
    }
}

/**
 * Loads the common strings previously stored in localStorage
 * @param locale
 * @returns {*}
 */
function getStoredStrings(locale) {
    let strings = null;
    if (storage) {
        strings = storage.getItem({
            storageType: "sessionStorage",
            key: `${STRINGS_STORAGE_KEY}${locale}`
        });
        events.publish(events.global.STRINGS_SERVICES_LOADED, strings);
    }
    return strings;
}

/**
 * Loads the common strings from the service
 *
 * @param locale
 * @returns {Promise<void>}
 */
async function getServerStrings(locale) {
    const {Url: urlCfg} = await getConfiguration(['Url']);
    let url = (urlCfg.commonStringsUrl || '').replace(
        "{locale}",
        locale
    );

    let strings = null;
    let response = await axios({ method: "GET", url: url });
    strings = response.data;
    saveServerStrings(strings, locale);

    return strings;
}

async function loadApplicationServerStrings(application, locale) {
    if (host === undefined) {
        host = RunConfigService.getBaseUrl();
    }
    let url =
    host +
    "/content/localization/strings/" +
    application +
    ".service." +
    locale +
    ".json";
    let remoteStrings;
    let response = await axios({ method: "GET", url: url });
    remoteStrings = response.data;
    saveApplicationServerStrings(application, remoteStrings, locale);
    return remoteStrings;
}

function saveApplicationServerStrings(application, applicationStrings, locale) {
    storage.setItem(
        { storageType: "sessionStorage", key: `${application}-strings-${locale}` },
        applicationStrings,
        { ttl: EXPIRE_TIME }
    );
}

function setHost(hostUrl = "") {
    host = hostUrl;
}

/**
 * Saves the common strings to localStorage for the specified ttl
 *
 * @param strings
 * @param locale
 */
function saveServerStrings(strings, locale) {
    storage.setItem(
        { storageType: "sessionStorage", key: `${STRINGS_STORAGE_KEY}${locale}` },
        strings,
        { ttl: EXPIRE_TIME }
    );
}
